import type { CSSProperties } from "react";
import type { DataGridPin } from "../columns/types";

const identCache = new Map<string, string>();

/**
 * Column id as a CSS custom-property fragment. Anything outside
 * `[A-Za-z0-9-]`, `_` included, becomes `_<hex>_`, so distinct ids never
 * collide.
 */
export const toCssIdent = (columnId: string): string => {
    const cached = identCache.get(columnId);
    if (cached !== undefined) {
        return cached;
    }
    let ident = "";
    for (const character of columnId) {
        ident += /[A-Za-z0-9-]/.test(character)
            ? character
            : `_${(character.codePointAt(0) ?? 0).toString(16)}_`;
    }
    identCache.set(columnId, ident);
    return ident;
};

export const columnWidthVar = (columnId: string) =>
    `--dg-col-${toCssIdent(columnId)}-w`;
export const columnStartVar = (columnId: string) =>
    `--dg-col-${toCssIdent(columnId)}-start`;
export const columnAfterVar = (columnId: string) =>
    `--dg-col-${toCssIdent(columnId)}-after`;

const styleCache = new Map<string, CSSProperties>();

/**
 * Stable inline style for a cell or header of a column: width from the size
 * var, plus the sticky inset for pinned columns. Cached so every cell of a
 * column shares one object.
 */
export const getColumnStyle = (
    columnId: string,
    pin: DataGridPin | false,
): CSSProperties => {
    const key = `${pin || "center"}:${columnId}`;
    const cached = styleCache.get(key);
    if (cached) {
        return cached;
    }
    const width = `var(${columnWidthVar(columnId)})`;
    const style: CSSProperties = { width };
    if (pin === "start") {
        style.insetInlineStart = `var(${columnStartVar(columnId)})`;
    }
    if (pin === "end") {
        style.insetInlineEnd = `var(${columnAfterVar(columnId)})`;
    }
    styleCache.set(key, style);
    return style;
};

/** Span of several leaf columns, for group headers. */
export const getSpanStyle = (columnIds: readonly string[]): CSSProperties => {
    const widths = columnIds.map((id) => `var(${columnWidthVar(id)})`);
    return { width: `calc(${widths.join(" + ")})` };
};
